import type { DocsService } from '@affine/core/modules/doc';
import type { TagService } from '@affine/core/modules/tag';
import type { WorkspaceService } from '@affine/core/modules/workspace';
import type { BlockModel, DocMeta } from '@blocksuite/affine/store';
import { Service } from '@toeverything/infra';
import { combineLatest, map, type Observable, of, switchMap } from 'rxjs';

import type { FilterProvider } from '../../provider';
import type { FilterParams } from '../../types';

export type ContentFilterKey = 'todo' | 'canvas' | 'project';

export interface ContentScanResult {
  version: number;
  hasCanvas: boolean;
  hasTodo: boolean;
}

export type ContentScanCache = Map<string, ContentScanResult>;

interface DocContentMatchOptions {
  cache: ContentScanCache;
  docId: string;
  filter: ContentFilterKey;
  meta: Partial<DocMeta>;
  tagIds: Set<string>;
  title: string;
  updatedAt: Date | number | undefined;
  workspace: WorkspaceService['workspace'];
}

const TODO_TEXT_PATTERN = /(^|[^A-Za-z0-9_])#?todo($|[^A-Za-z0-9_])/i;

export function hasTodoText(text: string): boolean {
  return TODO_TEXT_PATTERN.test(text);
}

function getBlockText(model: BlockModel): string {
  return model.text?.toString() ?? '';
}

function isUncheckedTodoBlock(model: BlockModel): boolean {
  if (model.flavour !== 'affine:list') return false;

  const props = model.props as {
    type?: string;
    checked?: boolean;
  };
  return props.type === 'todo' && props.checked === false;
}

export function blockTreeHasTodoMarker(models: BlockModel[]): boolean {
  const pending = [...models];
  while (pending.length > 0) {
    const model = pending.pop();
    if (!model) continue;

    if (isUncheckedTodoBlock(model) || hasTodoText(getBlockText(model))) {
      return true;
    }

    pending.push(...model.children);
  }
  return false;
}

export function blockTreeHasCanvasFrame(models: BlockModel[]): boolean {
  const pending = [...models];
  while (pending.length > 0) {
    const model = pending.pop();
    if (!model) continue;

    if (model.flavour === 'affine:frame') {
      return true;
    }

    pending.push(...model.children);
  }
  return false;
}

function getVersion(updatedAt: Date | number | undefined): number {
  return updatedAt instanceof Date ? updatedAt.getTime() : (updatedAt ?? 0);
}

function scanDocContent(
  workspace: WorkspaceService['workspace'],
  docId: string,
  updatedAt: Date | number | undefined,
  cache: ContentScanCache
): ContentScanResult | null {
  const version = getVersion(updatedAt);
  const cached = cache.get(docId);
  if (cached && cached.version === version) {
    return cached;
  }

  const bsDoc = workspace.docCollection.getDoc(docId)?.getStore({ id: docId });
  if (!bsDoc) {
    return null;
  }

  const rootChildren = bsDoc.root?.children ?? [];
  const result = {
    version,
    hasCanvas: blockTreeHasCanvasFrame(rootChildren),
    hasTodo: blockTreeHasTodoMarker(rootChildren),
  };
  cache.set(docId, result);
  return result;
}

export function docMatchesContentFilter({
  cache,
  docId,
  filter,
  meta,
  tagIds,
  title,
  updatedAt,
  workspace,
}: DocContentMatchOptions): boolean {
  if (filter === 'todo') {
    if (hasTodoText(title)) return true;
    if ((meta.tags ?? []).some(tagId => tagIds.has(tagId))) return true;
  }

  if (filter === 'project') {
    return (meta.tags ?? []).some(tagId => tagIds.has(tagId));
  }

  const scan = scanDocContent(workspace, docId, updatedAt, cache);
  if (!scan) return false;

  return filter === 'todo' ? scan.hasTodo : scan.hasCanvas;
}

function tagIdsByValue$(
  tagService: TagService,
  tagValue: string
): Observable<Set<string>> {
  return tagService.tagList.tags$.pipe(
    switchMap(tags => {
      if (tags.length === 0) {
        return of([]);
      }
      return combineLatest(
        tags.map(tag =>
          tag.value$.pipe(
            map(value => ({
              id: tag.id,
              isMatch: value.trim().toLowerCase() === tagValue,
            }))
          )
        )
      );
    }),
    map(tags => new Set(tags.filter(tag => tag.isMatch).map(tag => tag.id)))
  );
}

function docContentRows$(docsService: DocsService) {
  return docsService.list.docs$.pipe(
    switchMap(docs => {
      if (docs.length === 0) {
        return of([]);
      }
      return combineLatest(
        docs.map(doc =>
          combineLatest([doc.title$, doc.meta$, doc.updatedAt$]).pipe(
            map(([title, meta, updatedAt]) => ({
              docId: doc.id,
              meta,
              title,
              updatedAt,
            }))
          )
        )
      );
    })
  );
}

abstract class ContentFilterProvider extends Service implements FilterProvider {
  private readonly cache: ContentScanCache = new Map();

  protected abstract readonly key: ContentFilterKey;

  constructor(
    protected readonly docsService: DocsService,
    protected readonly workspaceService: WorkspaceService,
    protected readonly tagService?: TagService
  ) {
    super();
  }

  filter$(params: FilterParams): Observable<Set<string>> {
    if (params.method !== 'is') {
      throw new Error(`Unsupported method: ${params.method}`);
    }

    const include = params.value !== 'false';
    const tags$ = this.tagService
      ? tagIdsByValue$(this.tagService, this.key)
      : of(null);

    return combineLatest([docContentRows$(this.docsService), tags$]).pipe(
      map(([rows, tagIds]) => {
        const matched = rows
          .filter(row => {
            const isMatch = docMatchesContentFilter({
              ...row,
              cache: this.cache,
              filter: this.key,
              tagIds: tagIds ?? new Set(),
              workspace: this.workspaceService.workspace,
            });
            return include ? isMatch : !isMatch;
          })
          .map(row => row.docId);

        return new Set(matched);
      })
    );
  }
}

export class TodoFilterProvider extends ContentFilterProvider {
  protected readonly key = 'todo';

  constructor(
    docsService: DocsService,
    tagService: TagService,
    workspaceService: WorkspaceService
  ) {
    super(docsService, workspaceService, tagService);
  }
}

export class CanvasFilterProvider extends ContentFilterProvider {
  protected readonly key = 'canvas';

  constructor(docsService: DocsService, workspaceService: WorkspaceService) {
    super(docsService, workspaceService);
  }
}

export class ProjectFilterProvider extends ContentFilterProvider {
  protected readonly key = 'project';

  constructor(
    docsService: DocsService,
    tagService: TagService,
    workspaceService: WorkspaceService
  ) {
    super(docsService, workspaceService, tagService);
  }
}
